import {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import scssStyles from "./SelectRow.module.scss";
import { Icon } from "../Icon/icon";

interface SelectRowProps
  extends Omit<HTMLAttributes<HTMLFieldSetElement>, "onChange"> {
  tabs?: boolean;
  dark?: boolean;
  label: string;
  hideLabel?: boolean;
  name: string;
  value: string | number | boolean;
  options?: {
    icon?: string;
    value: string | number | boolean;
    label: string | ReactNode;
    hideLabel?: boolean;
  }[];
  disabled?: boolean;
  onChange: (value: string | number | boolean) => void;
  lowerOpacity?: boolean;
  styles?: {
    fieldset?: CSSProperties;
    legend?: CSSProperties;
    container?: CSSProperties;
    input?: CSSProperties;
    label?: CSSProperties;
  };
}

export function SelectRow({
  tabs,
  dark,
  label,
  hideLabel,
  name,
  value,
  options,
  disabled,
  onChange,
  lowerOpacity,
  styles,
  ...props
}: SelectRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedLabel = containerRef.current?.querySelector(
      `label.${scssStyles.selected}`,
    ) as HTMLElement;
    const container = containerRef.current;

    if (selectedLabel && container) {
      const containerRect = container.getBoundingClientRect();
      const selectedRect = selectedLabel.getBoundingClientRect();
      const offset =
        selectedRect.left -
        containerRect.left -
        containerRect.width / 2 +
        selectedRect.width / 2;

      container.scrollTo({
        left: container.scrollLeft + offset,
        behavior: "smooth",
      });
    }
  }, [value]);

  return (
    <fieldset
      disabled={disabled}
      className={`${scssStyles.fieldset} ${disabled ? scssStyles.disabled : ""}`}
      style={styles?.fieldset}
      {...props}
    >
      <legend className={hideLabel ? "sr-only" : ""} style={styles?.legend}>
        {label}
      </legend>
      {options && (
        <div
          ref={containerRef}
          className={`${scssStyles.container} ${dark && scssStyles.dark} ${
            tabs ? scssStyles.tabs : ""
          } ${lowerOpacity ? scssStyles.lowerOpacity : ""} absoluteMaxWidth`}
          style={styles?.container}
        >
          {options.map((opt) => (
            <label
              key={opt.value.toString()}
              className={value === opt.value ? scssStyles.selected : ""}
              style={styles?.label}
            >
              <input
                type="radio"
                name={name}
                value={
                  typeof opt.value === "boolean"
                    ? opt.value.toString()
                    : opt.value
                }
                checked={value === opt.value}
                onChange={(e) => {
                  if (tabs) e.stopPropagation();
                  e.preventDefault();
                  onChange(opt.value);
                }}
                style={styles?.input}
              />
              {opt.icon && <Icon name={opt.icon} />}{" "}
              <span
                className={opt.hideLabel ? "sr-only" : ""}
                style={styles?.label}
              >
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}
